// src/contexts/UserContext.tsx
import React, { createContext, useContext, useEffect, useRef } from 'react';
import {
   onAuthStateChanged,
   signInWithEmailAndPassword,
   createUserWithEmailAndPassword,
   signOut,
   sendPasswordResetEmail,
   updateProfile,
   User as FirebaseUser,
} from 'firebase/auth';
import {
   doc,
   setDoc,
   getDoc,
   updateDoc,
   serverTimestamp,
   collection,
   query,
   where,
   getDocs,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import webSocketService from '../services/WebSocketService';
import onlineStatusService from '../services/OnlineStatusService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
   loginUser,
   logoutUser,
   setLoading,
   setError,
   updateUser,
   setAuthChecked,
   selectCurrentUser,
   selectIsAuthenticated,
   selectUserLoading,
   selectUserError,
   User,
} from '../store/slices/userSlice';
import { clearSubscription } from '../store/slices/subscriptionSlice';
import { checkInvitedUser, markInvitedUserAsUsed } from '../services/invitedUserService';

interface UserContextType {
   currentUser: User | null;
   isAuthenticated: boolean;
   isLoading: boolean;
   error: string | null;
   // hasActiveSubscription: boolean;
   login: (email: string, password: string) => Promise<void>;
   register: (email: string, password: string, displayName: string) => Promise<void>;
   logout: () => Promise<void>;
   resetPassword: (email: string) => Promise<void>;
   updateUserProfile: (data: Partial<User>) => Promise<void>;
   getUserById: (userId: string) => Promise<User | null>;
   searchUsersByEmail: (email: string) => Promise<User[]>;
   refreshSubscription: () => Promise<void>;
   createCheckoutSession: () => Promise<string>;
   createPortalSession: () => Promise<string>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const dispatch = useAppDispatch();

   // Use Redux state instead of local state
   const currentUser = useAppSelector(selectCurrentUser);
   const isAuthenticated = useAppSelector(selectIsAuthenticated);
   const isLoading = useAppSelector(selectUserLoading);
   const error = useAppSelector(selectUserError);

   const isAuthenticatedRef = useRef(false);

   // Fetch subscription status from the server
   const [lastSubscriptionCheck, setLastSubscriptionCheck] = React.useState<number>(0);
   const [isRefreshingSubscription, setIsRefreshingSubscription] = React.useState<boolean>(false);

   // Helper function to get proper display name from user profile
   const getProperDisplayName = async (userId: string, fallbackEmail?: string): Promise<string> => {
      try {
         // First, try to get from userProfiles collection (most up-to-date) - use direct doc access
         const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
         if (userProfileDoc.exists()) {
            const profileData = userProfileDoc.data();
            const displayName =
               profileData.displayName || profileData.fullName || profileData.firstName;
            if (displayName && displayName !== 'Unknown User' && !displayName.startsWith('User ')) {
               return displayName;
            }
         }

         // Fallback to users collection
         const userDoc = await getDoc(doc(db, 'users', userId));
         if (userDoc.exists()) {
            const userData = userDoc.data();
            const displayName = userData.displayName || userData.fullName || userData.firstName;
            if (displayName && displayName !== 'Unknown User' && !displayName.startsWith('User ')) {
               return displayName;
            }
         }

         // Final fallback to email
         return fallbackEmail?.split('@')[0] || 'User';
      } catch (error) {
         console.error('Error fetching proper display name:', error);
         return fallbackEmail?.split('@')[0] || 'User';
      }
   };

   useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
         dispatch(setLoading(true));

         try {
            //prevent updating user if the sign in is used for google photos sync

            if (user) {
               const userDoc = await getDoc(doc(db, 'users', user.uid));

               if (userDoc.exists()) {
                  await updateDoc(doc(db, 'users', user.uid), {
                     lastLogin: serverTimestamp(),
                  });

                  const userProfileDoc = await getDoc(doc(db, 'userProfiles', user.uid));
                  const userProfileData = userProfileDoc.data();
                  const userData = userDoc.data();

                  // Get proper display name from user profile
                  const properDisplayName = await getProperDisplayName(user.uid, user.email || '');

                  const userInfo: User = {
                     ...userProfileData,
                     uid: user.uid,
                     photoURL: userProfileData?.photoURL || null,
                     email: user.email,
                     displayName: properDisplayName,
                     emailVerified: user.emailVerified,
                     phoneNumber: user.phoneNumber,
                     fullName: userProfileData?.fullName || userData?.fullName,
                     accountType: userData?.accountType,
                     createdAt:
                        userData.createdAt && typeof userData.createdAt.toDate === 'function'
                           ? userData.createdAt.toDate()
                           : userData.createdAt instanceof Date
                             ? userData.createdAt
                             : new Date(),
                  };

                  dispatch(loginUser(userInfo));
                  isAuthenticatedRef.current = true;

                  // Subscription fetching is now handled by protected routes
                  // This allows faster login and better separation of concerns

                  // Set user online status and initialize OnlineStatusService (non-blocking)
                  setTimeout(async () => {
                     try {
                        // Initialize OnlineStatusService with current user info
                        onlineStatusService.initializeCurrentUser(user.uid, properDisplayName);

                        // Also update WebSocket status (backup)
                        await webSocketService.updateUserStatus(
                           user.uid,
                           properDisplayName, // Use proper display name
                           true
                        );

                        console.log(
                           '🔥 OnlineStatusService initialized for user:',
                           properDisplayName
                        );
                     } catch (error) {
                        console.warn('Failed to initialize online status services:', error);
                     }
                  }, 100);
               }
            } else {
               // User logged out - get previous user before clearing state
               const previousUser = currentUser;
               dispatch(logoutUser());
               dispatch(clearSubscription()); // Clear subscription data on logout
               isAuthenticatedRef.current = false;

               // Set user offline status and cleanup OnlineStatusService if we had a previous user (non-blocking)
               if (previousUser) {
                  setTimeout(async () => {
                     try {
                        // Set user offline in OnlineStatusService
                        onlineStatusService.setCurrentUserOffline(
                           previousUser.uid,
                           previousUser.displayName || previousUser.email?.split('@')[0] || 'User'
                        );

                        // Also update WebSocket status (backup)
                        await webSocketService.updateUserStatus(
                           previousUser.uid,
                           previousUser.displayName || previousUser.email?.split('@')[0] || 'User',
                           false
                        );

                        console.log(
                           '🧹 OnlineStatusService cleaned up for user:',
                           previousUser.displayName
                        );
                     } catch (error) {
                        console.warn('Failed to cleanup online status services:', error);
                     }
                  }, 100);
               }
            }
         } catch (err) {
            dispatch(setError('Failed to authenticate user'));
            isAuthenticatedRef.current = false;
         } finally {
            dispatch(setAuthChecked());
         }
      });

      return () => unsubscribe();
   }, [dispatch]);

   const login = async (email: string, password: string) => {
      dispatch(setLoading(true));
      try {
         const result = await signInWithEmailAndPassword(auth, email, password);

         // Update last login timestamp only (check if document exists first)
         // Note: We don't update invitation status during login - only active invitations matter
         const userDocRef = doc(db, 'users', result.user.uid);
         const userDoc = await getDoc(userDocRef);

         if (userDoc.exists()) {
            await updateDoc(userDocRef, {
               lastLogin: serverTimestamp(),
            });
         } else {
            // Create user document if it doesn't exist (shouldn't happen but safety check)
            console.warn(`User document missing for ${result.user.uid}, creating it now`);

            // Check if user was invited before creating document
            let wasUserInvited = false;
            if (result.user.email) {
               try {
                  // Check for active invitations
                  const hasActiveInvitation = await checkInvitedUser(result.user.email);

                  // For missing user documents during login, we should also check invitation history
                  // This handles cases where user was invited but document was lost/recreated
                  if (!hasActiveInvitation) {
                     // Check for ANY invitations (including used ones) via the proper API endpoint
                     try {
                        const response = await fetch(
                           `${process.env.REACT_APP_API_URL}/api/invited-users/details`,
                           {
                              method: 'POST',
                              headers: {
                                 'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({ email: result.user.email }),
                           }
                        );

                        if (response.ok) {
                           const invitationData = await response.json();
                           // If user has ANY invitation history (active or used), they were originally invited
                           wasUserInvited = invitationData.hasInvitations || false;
                           console.log(
                              '🔍 UserContext: Checked invitation history for missing document:',
                              {
                                 email: result.user.email,
                                 hasInvitations: invitationData.hasInvitations,
                                 wasUserInvited,
                              }
                           );
                        }
                     } catch (historyError) {
                        console.error('Error checking invitation history:', historyError);
                        // Fallback: assume not invited if we can't check
                        wasUserInvited = false;
                     }
                  } else {
                     wasUserInvited = true;
                  }
               } catch (error) {
                  console.error(
                     'Error checking invitation status during document creation:',
                     error
                  );
               }
            }

            await setDoc(userDocRef, {
               uid: result.user.uid,
               email: result.user.email,
               displayName: result.user.displayName || '',
               createdAt: serverTimestamp(),
               lastLogin: serverTimestamp(),
               invited: wasUserInvited, // Set based on actual invitation status
            });

            console.log(
               '✅ UserContext: Created missing user document with wasInvited:',
               wasUserInvited
            );
         }

         dispatch(setError(null));
      } catch (err) {
         console.error('Login error:', err);
         dispatch(setError('Invalid email or password'));
         throw err;
      }
   };

   const register = async (email: string, password: string, displayName: string) => {
      dispatch(setLoading(true));
      try {
         // Check if user is invited before registration
         const invitedUserCheck = await checkInvitedUser(email);
         console.log('🔍 Signup invitation check:', {
            email,
            hasActiveInvitation: invitedUserCheck,
            note: 'Invitation allows signup without subscription, but will be marked as used',
         });

         const result = await createUserWithEmailAndPassword(auth, email, password);

         // Update profile with displayName
         await updateProfile(result.user, { displayName });

         // Create user document in Firestore with permanent invitation flag
         try {
            await setDoc(doc(db, 'users', result.user.uid), {
               uid: result.user.uid,
               email,
               displayName,
               createdAt: serverTimestamp(),
               lastLogin: serverTimestamp(),
               invited: invitedUserCheck, // Set invited flag based on invitation status
            });
            console.log(`✅ Created user document for ${result.user.uid}`);
         } catch (userDocError) {
            console.error(
               `❌ Failed to create user document for ${result.user.uid}:`,
               userDocError
            );
            throw new Error('Failed to create user profile');
         }

         try {
            await setDoc(doc(db, 'userProfiles', result.user.uid), {
               uid: result.user.uid,
               displayName,
               email,
               photoURL: '',
               createdAt: new Date().toISOString(),
               updatedAt: new Date().toISOString(),
               invited: invitedUserCheck, // Set invited flag based on invitation status
            });
            console.log(`✅ Created user profile for ${result.user.uid}`);
         } catch (profileError) {
            console.error(`❌ Failed to create user profile for ${result.user.uid}:`, profileError);
            // Don't throw here as the main user document was created successfully
         }

         // If user was invited, mark the invitation as used (this removes their bypass privilege)
         if (invitedUserCheck) {
            try {
               await markInvitedUserAsUsed(email);
               console.log('✅ Marked invitation as used after successful signup:', email);

               // Add a small delay to ensure the backend has processed the update
               await new Promise((resolve) => setTimeout(resolve, 1000));
               console.log('⏰ Waited for invitation status to be processed');
            } catch (error) {
               console.error('Error marking invitation as used:', error);
            }
         }

         dispatch(setError(null));
      } catch (err) {
         console.error('Registration error:', err);
         dispatch(setError('Failed to register user'));
         throw err;
      }
   };

   const logout = async () => {
      try {
         // Set user offline status before signing out
         if (currentUser) {
            try {
               // Set user offline in OnlineStatusService
               onlineStatusService.setCurrentUserOffline(
                  currentUser.uid,
                  currentUser.displayName || currentUser.email || ''
               );

               // Also update WebSocket status (backup)
               await webSocketService.updateUserStatus(
                  currentUser.uid,
                  currentUser.displayName || currentUser.email || '',
                  false
               );

               console.log(
                  '🧹 OnlineStatusService cleaned up during logout for user:',
                  currentUser.displayName
               );
            } catch (error) {
               console.warn('Failed to cleanup online status services during logout:', error);
            }
         }

         await signOut(auth);
         dispatch(setError(null));
         isAuthenticatedRef.current = false;
      } catch (err) {
         console.error('Logout error:', err);
         dispatch(setError('Failed to log out'));
         throw err;
      }
   };

   const resetPassword = async (email: string) => {
      dispatch(setLoading(true));
      try {
         await sendPasswordResetEmail(auth, email);
         dispatch(setError(null));
      } catch (err) {
         console.error('Password reset error:', err);
         dispatch(setError('Failed to send password reset email'));
         throw err;
      } finally {
         dispatch(setAuthChecked());
      }
   };

   const updateUserProfile = async (data: Partial<User>) => {
      if (!currentUser) throw new Error('No authenticated user');

      dispatch(setLoading(true));
      try {
         // Update in Firestore
         await updateDoc(doc(db, 'users', currentUser.uid), {
            ...data,
            updatedAt: serverTimestamp(),
         });

         // Update Redux state
         dispatch(updateUser(data));
         dispatch(setError(null));
      } catch (err) {
         console.error('Profile update error:', err);
         dispatch(setError('Failed to update profile'));
         throw err;
      } finally {
         dispatch(setAuthChecked());
      }
   };

   const getUserById = async (userId: string): Promise<User | null> => {
      try {
         const userDoc = await getDoc(doc(db, 'users', userId));
         if (userDoc.exists()) {
            const userData = userDoc.data();
            const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
            const userProfileData = userProfileDoc.data();

            // Get proper display name from user profile
            const properDisplayName = await getProperDisplayName(userId, userData.email || '');

            return {
               uid: userId,
               email: userData.email || null,
               displayName: properDisplayName,
               photoURL: userProfileData?.photoURL || null,
               emailVerified: userData.emailVerified || false,
               phoneNumber: userData.phoneNumber || null,
               fullName: userProfileData?.fullName || userData?.fullName,
               accountType: userData?.accountType,
               createdAt:
                  userData.createdAt && typeof userData.createdAt.toDate === 'function'
                     ? userData.createdAt.toDate()
                     : userData.createdAt instanceof Date
                       ? userData.createdAt
                       : new Date(),
               updatedAt:
                  userData.lastLogin && typeof userData.lastLogin.toDate === 'function'
                     ? userData.lastLogin.toDate()
                     : userData.lastLogin instanceof Date
                       ? userData.lastLogin
                       : new Date(),
            };
         }
         return null;
      } catch (error) {
         console.error('Error fetching user by ID:', error);
         return null;
      }
   };

   const searchUsersByEmail = async (email: string): Promise<User[]> => {
      try {
         // Search for users where email contains the search string
         const q = query(
            collection(db, 'users'),
            where('email', '>=', email),
            where('email', '<=', email + '\uf8ff')
         );

         const querySnapshot = await getDocs(q);
         const users: User[] = [];

         for (const docSnapshot of querySnapshot.docs) {
            const data = docSnapshot.data();
            const userProfileDoc = await getDoc(doc(db, 'userProfiles', docSnapshot.id));
            const userProfileData = userProfileDoc.data();

            users.push({
               uid: docSnapshot.id,
               email: data.email || null,
               displayName: data.displayName || null,
               photoURL: userProfileData?.photoURL || null,
               emailVerified: data.emailVerified || false,
               phoneNumber: data.phoneNumber || null,
               fullName: userProfileData?.fullName || data?.fullName,
               accountType: data?.accountType,
               createdAt:
                  data.createdAt && typeof data.createdAt.toDate === 'function'
                     ? data.createdAt.toDate()
                     : data.createdAt instanceof Date
                       ? data.createdAt
                       : new Date(),
               updatedAt:
                  data.lastLogin && typeof data.lastLogin.toDate === 'function'
                     ? data.lastLogin.toDate()
                     : data.lastLogin instanceof Date
                       ? data.lastLogin
                       : new Date(),
            });
         }

         return users;
      } catch (err) {
         console.error('Search users error:', err);
         throw err;
      }
   };

   // Legacy function - subscription fetching is now handled by SubscriptionProvider
   const refreshSubscription = async (): Promise<void> => {
      console.warn(
         'UserContext.refreshSubscription: This function is deprecated. Subscription data is now managed by SubscriptionProvider.'
      );
      // No-op - subscription data is managed by SubscriptionProvider
   };

   // Create a checkout session for subscription
   const createCheckoutSession = async (): Promise<string> => {
      if (!currentUser) throw new Error('Not authenticated');

      try {
         // Return direct URL to Stripe checkout page
         return 'https://buy.stripe.com/28o9By85f6SH4yQeUV';
      } catch (err) {
         console.error('Error creating checkout session:', err);
         throw err;
      }
   };

   // Create a portal session for managing subscription
   const createPortalSession = async (): Promise<string> => {
      if (!currentUser) throw new Error('Not authenticated');

      try {
         // Return direct URL to Stripe billing portal
         return 'https://buy.stripe.com/28o9By85f6SH4yQeUV';
      } catch (err) {
         console.error('Error creating portal session:', err);
         throw err;
      }
   };

   // Check for subscription query params on page load
   React.useEffect(() => {
      const checkSessionId = async () => {
         const urlParams = new URLSearchParams(window.location.search);
         const sessionId = urlParams.get('session_id');

         if (sessionId && currentUser) {
            // Clear the URL parameter
            window.history.replaceState({}, document.title, window.location.pathname);

            // Note: Subscription refresh after checkout is now handled by SubscriptionProvider
            // The SuccessSubscription page will force refresh subscription data
         }
      };

      if (currentUser) {
         checkSessionId();
         // refreshSubscription() removed - now handled by SubscriptionProvider
      }
   }, [currentUser]);

   const value = {
      currentUser,
      isAuthenticated: isAuthenticatedRef.current || isAuthenticated,
      isLoading,
      error,
      // hasActiveSubscription,
      login,
      register,
      logout,
      resetPassword,
      updateUserProfile,
      getUserById,
      searchUsersByEmail,
      refreshSubscription,
      createCheckoutSession,
      createPortalSession,
   };

   return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
   const context = useContext(UserContext);
   if (context === undefined) {
      throw new Error('useUser must be used within a UserProvider');
   }
   return context;
};

// Export the User type for other components to use
export type { User };
